

import React, { createContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { ExpenseEntry } from '../types';
import { API_BASE_URL } from '../constants';


interface ExpenseContextType {
  allExpenses: ExpenseEntry[];
  loading: boolean;
  saveExpense: (expenseData: Partial<ExpenseEntry> | Partial<ExpenseEntry>[]) => Promise<{ success: boolean; message?: string }>;
  deleteExpense: (expenseId: string) => Promise<void>;
  deleteExpenses: (expenseIds: string[]) => Promise<void>;
  deleteExpensesByProjectId: (projectId: string) => Promise<void>; // This should not be needed with CASCADE DELETE
}

export const ExpenseContext = createContext<ExpenseContextType | undefined>(undefined);

interface ExpenseProviderProps {
  children: ReactNode;
}

export const ExpenseProvider: React.FC<ExpenseProviderProps> = ({ children }) => {
    const [allExpenses, setAllExpenses] = useState<ExpenseEntry[]>([]);
    const [loading, setLoading] = useState(true);

    const fetchExpenses = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/expenses`);
            if (!response.ok) throw new Error('Failed to fetch expenses');
            const data: ExpenseEntry[] = await response.json();
            setAllExpenses(data);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchExpenses();
    }, [fetchExpenses]);

    const saveExpense = useCallback(async (expenseData: Partial<ExpenseEntry> | Partial<ExpenseEntry>[]): Promise<{ success: boolean; message?: string }> => {
        // Handle single expense update (PUT)
        if (!Array.isArray(expenseData) && expenseData.id) {
            try {
                const response = await fetch(`${API_BASE_URL}/expenses/${expenseData.id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(expenseData),
                });
                const savedData = await response.json();

                if (!response.ok) {
                    return { success: false, message: savedData.message || 'Falha ao atualizar despesa.' };
                }

                setAllExpenses(current => current.map(e => e.id === savedData.id ? savedData : e));
                return { success: true };
            } catch (error) {
                console.error("Erro ao atualizar despesa:", error);
                 if (error instanceof SyntaxError) {
                    return { 
                        success: false, 
                        message: 'Falha de comunicação com o servidor. Isso geralmente acontece se um anexo for muito grande. Tente um arquivo menor.' 
                    };
                }
                return { success: false, message: (error as Error).message || 'Erro de conexão.' };
            }
        }

        // Handle expense creation (POST), both single and bulk
        let expensesToCreate: Partial<ExpenseEntry>[];

        if (Array.isArray(expenseData)) {
            // Bulk import from ImportacaoPage
            expensesToCreate = expenseData;
        } else {
            // Single expense creation, potentially recurring
            if (expenseData.isRecurring && expenseData.recurrenceMonths && expenseData.recurrenceMonths > 0) {
                // Generate recurring entries
                expensesToCreate = [];
                const baseDate = new Date(`${expenseData.month}-15T12:00:00Z`);
                const totalMonthsToRepeat = expenseData.recurrenceMonths;

                for (let i = 0; i < totalMonthsToRepeat + 1; i++) {
                    const loopDate = new Date(baseDate);
                    loopDate.setUTCMonth(baseDate.getUTCMonth() + i);

                    const newExpense: Partial<ExpenseEntry> = {
                        ...expenseData,
                        id: `exp_${Date.now()}_${Math.random()}`,
                        month: loopDate.toISOString().slice(0, 7),
                        description: `${expenseData.description || ''} (Parcela ${i + 1}/${totalMonthsToRepeat + 1})`.trim(),
                        isRecurring: false,
                        recurrenceMonths: undefined,
                    };
                    expensesToCreate.push(newExpense);
                }
            } else {
                // Standard single creation
                expensesToCreate = [expenseData];
            }
        }
        
        if (expensesToCreate.length === 0) {
            return { success: true }; // Nothing to do
        }

        try {
            const response = await fetch(`${API_BASE_URL}/expenses`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(expensesToCreate),
            });
            const savedExpenses = await response.json();

            if (!response.ok) {
                return { success: false, message: savedExpenses.message || 'Falha ao adicionar despesa(s).' };
            }

            // The backend returns an array for POST, even for a single item.
            setAllExpenses(current => [...savedExpenses, ...current]);
            return { success: true };
        } catch (error) {
            console.error("Erro ao salvar despesa(s):", error);
            if (error instanceof SyntaxError) {
                return { 
                    success: false, 
                    message: 'Falha de comunicação com o servidor. Isso geralmente acontece se um anexo for muito grande. Tente um arquivo menor.' 
                };
            }
            return { success: false, message: (error as Error).message || 'Erro de conexão.' };
        }
    }, []);

    const deleteExpense = useCallback(async (expenseId: string) => {
        try {
            const response = await fetch(`${API_BASE_URL}/expenses/${expenseId}`, {
                method: 'DELETE',
            });
            if (!response.ok) throw new Error('Failed to delete expense');
            const result = await response.json();
            if (result.success) {
                setAllExpenses(current => current.filter(e => e.id !== expenseId));
            }
        } catch (error) {
            console.error(error);
        }
    }, []);

    const deleteExpenses = useCallback(async (expenseIds: string[]) => {
        if (!expenseIds || expenseIds.length === 0) return;
        try {
            const response = await fetch(`${API_BASE_URL}/expenses/delete-bulk`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ids: expenseIds }),
            });
            if (!response.ok) throw new Error('Failed to delete bulk expenses');
            const result = await response.json();
            if (result.success) {
                setAllExpenses(current => current.filter(e => !expenseIds.includes(e.id)));
            }
        } catch (error) {
            console.error(error);
        }
    }, []);
    
    // This function becomes less critical with ON DELETE CASCADE in the DB,
    // but can be kept for frontend state consistency if needed.
    const deleteExpensesByProjectId = useCallback(async (projectId: string) => {
        setAllExpenses(current => current.filter(e => e.projectId !== projectId));
    }, []);

    return (
        <ExpenseContext.Provider value={{ allExpenses, loading, saveExpense, deleteExpense, deleteExpenses, deleteExpensesByProjectId }}>
            {children}
        </ExpenseContext.Provider>
    );
};